import React, { useCallback, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import CommonTabSwitcher from 'components/commonTabSwitcher/CommonTabSwitcher';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { sideBarRoutes } from 'utils/StaticData';
import { FaBuilding, FaHandshake, FaSitemap, FaUser } from 'react-icons/fa';
import EInvoiceList from './EInvoiceList';
import SalesPartnerInvoiceList from './SalesPartnerInvoiceList';
import OrganizationInvoiceList from './OrganizationInvoiceList';

const EInvoiceTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminDetails } = useSelector((state) => state.authSlice);

  const role = adminDetails?.role;
  const isAdminOrDealer =
    role === sideBarRoutes?.Admin || role === sideBarRoutes?.Dealer;
  // Sales Partner tab: Admin/Dealer see all; a Seller sees their own.
  const canSalesPartner = isAdminOrDealer || role === sideBarRoutes?.Seller;
  // Organization tab: Admin/Dealer see all; an Organization sees their own.
  const canOrganization =
    isAdminOrDealer || role === sideBarRoutes?.Organization;

  // Tab set is role-dependent, so keep each tab paired with its content and
  // render by the active definition (not a hard-coded index).
  const tabDefs = useMemo(
    () => [
      {
        label: 'Merchant Invoices (B2B)',
        icon: FaBuilding,
        render: () => <EInvoiceList invoiceType="B2B" isActive={true} />,
      },
      {
        label: 'Customer Invoices (B2C)',
        icon: FaUser,
        render: () => <EInvoiceList invoiceType="B2C" isActive={true} />,
      },
      ...(canSalesPartner
        ? [
            {
              label: 'Sales Partner Invoices',
              icon: FaHandshake,
              render: () => <SalesPartnerInvoiceList isActive={true} />,
            },
          ]
        : []),
      ...(canOrganization
        ? [
            {
              label: 'Organization Invoices',
              icon: FaSitemap,
              render: () => <OrganizationInvoiceList isActive={true} />,
            },
          ]
        : []),
    ],
    [canSalesPartner, canOrganization],
  );

  const tabIndex = useMemo(() => {
    const t = searchParams.get('tab');
    const parsed = t ? Number(t) : 0;
    const maxTab = tabDefs.length - 1;
    return parsed > maxTab || parsed < 0 ? 0 : parsed;
  }, [searchParams, tabDefs.length]);

  const setTabIndex = useCallback(
    (index) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (index === 0) {
            next.delete('tab');
          } else {
            next.set('tab', String(index));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const tabs = tabDefs.map(({ label, icon }) => ({ label, icon }));

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      <CommonTabSwitcher
        tabs={tabs}
        activeIndex={tabIndex}
        onChange={setTabIndex}
      />
      {tabDefs[tabIndex]?.render()}
    </Box>
  );
};

export default EInvoiceTabs;
